'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ResolutionTimeline } from '@/lib/components/Event/ResolutionTimeline';
import { EventHistoryCard } from '@/lib/components/EventHistoryCard';
import { EventHistoryTable } from '@/lib/components/EventHistoryTable';
import { EventOpenOrdersList } from '@/lib/components/EventOpenOrdersList';
import { EventOpenOrdersTable } from '@/lib/components/EventOpenOrdersTable';
import { EventOpenPositionsList } from '@/lib/components/EventOpenPositionsList';
import { EventOpenPositionsTable } from '@/lib/components/EventOpenPositionsTable';
import { OrderBook } from '@/lib/components/OrderBook/OrderBook';
import { OutcomeChart } from '@/lib/components/Outcomes/OutcomeChart';
import {
  useTradingData,
  useTradingPositionsCount,
} from '@/lib/components/Trading/TradingDataContext';
import { MarketItem } from '@/lib/types/markets/market';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { isOutcomeActive } from '@/lib/utils/outcomePrice';

type OutcomeTabsProps = {
  outcome: MarketItem;
  showChartTab?: boolean;
  showPositionsTabs?: boolean;
  hideOrderBookTab?: boolean; // Hide Order Book tab if it's displayed separately
  isEventConcluded?: boolean; // Only show Resolution tab when event has ended
  className?: string;
};

export const OutcomeTabs = ({
  outcome,
  showChartTab = false,
  showPositionsTabs = false,
  hideOrderBookTab = false,
  isEventConcluded = false,
  className,
}: OutcomeTabsProps) => {
  const t = useTranslations('event');
  const outcomeId = outcome.id;

  // Get trading data from context
  // Data is automatically loaded for selectedOutcomeId via TradingDataProvider
  const { hasHistory, hasOrders, activeOrders: activeOrdersData } = useTradingData(outcomeId);
  const positionsCount = useTradingPositionsCount(outcomeId);
  const ordersCount = activeOrdersData?.items.length ?? 0;

  const isResolved = outcome.status === 'RESOLVED';

  const tabs = useMemo(() => {
    const tabsList: { id: string; label: string }[] = [];

    // Show Open Positions tab if there are positions
    if (showPositionsTabs && positionsCount > 0) {
      tabsList.push({
        id: 'openPositions',
        label: t('tabs.openPositions', { count: positionsCount }),
      });
    }

    // Only add Order Book tab if not hidden (when displayed separately) and trading is
    // open: when it stops, trading-core cancels every order and the book is empty.
    if (!hideOrderBookTab && !isEventConcluded && isOutcomeActive(outcome)) {
      tabsList.push({ id: 'orderBook', label: t('orderBook') });
    }

    // Show Open Orders tab if data exists and outcome is not resolved
    if (hasOrders === true && !isResolved) {
      tabsList.push({ id: 'openOrders', label: t('tabs.openOrders', { count: ordersCount }) });
    }

    if (showChartTab) {
      tabsList.push({ id: 'chart', label: t('tabs.chart') });
    }

    // Show History tab if data exists (independent of showPositionsTabs)
    if (hasHistory === true) {
      tabsList.push({ id: 'history', label: t('tabs.history') });
    }

    // Show Resolution tab only if outcome has a winning resolution
    const hasWinningResolution =
      outcome.r && outcome.r.length > 0 && outcome.r.some((r) => r.isWin);
    if (isEventConcluded && hasWinningResolution) {
      tabsList.push({ id: 'resolution', label: t('tabs.resolution') });
    }

    return tabsList;
  }, [
    showChartTab,
    showPositionsTabs,
    hasHistory,
    hasOrders,
    ordersCount,
    positionsCount,
    hideOrderBookTab,
    isResolved,
    isEventConcluded,
    outcome,
    t,
  ]);

  // Calculate default tab based on available tabs
  const defaultTab = useMemo(() => {
    if (tabs.length === 0) return 'orderBook'; // Fallback
    // Priority: orderBook first, then others as fallback
    const orderBookTab = tabs.find((t) => t.id === 'orderBook');
    if (orderBookTab) return 'orderBook';
    return tabs[0].id; // Fallback to first tab
  }, [tabs]);

  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  // Ensure activeTab is valid - if it's not in the tabs list, switch to first available tab
  useEffect(() => {
    const tabIds = tabs.map((tab) => tab.id);
    if (!tabIds.includes(activeTab)) {
      setActiveTab(tabIds[0] || defaultTab);
    }
  }, [activeTab, tabs, defaultTab]);

  const tabButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);

  useLayoutEffect(() => {
    const btn = tabButtonRefs.current[activeIndex];
    if (btn) {
      setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
    } else {
      setIndicator(null);
    }
  }, [activeIndex]);

  // Don't render the tabs block if there are no tabs to show
  if (tabs.length === 0) {
    return null;
  }

  const isSingleOrderBookTab = tabs.length === 1 && tabs[0].id === 'orderBook';

  return (
    <div className={cn('gap-0 p-0 pt-3 pb-4', className)}>
      {isSingleOrderBookTab ? (
        <div className="flex items-center gap-1 border-b border-v2-border pb-3 text-v2-text-primary">
          <span className="text-[15px] font-normal leading-[19px]">{tabs[0].label}</span>
          <Icon name="ArrowSmallDown" size={24} className="shrink-0 text-v2-text-secondary" />
        </div>
      ) : (
        <div className="scrollbar-hide relative mb-4 flex items-center gap-5 overflow-x-auto">
          {tabs.map((tab, index) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  tabButtonRefs.current[index] = el;
                }}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'cursor-pointer whitespace-nowrap pt-1 pb-[6px] outline-none',
                  'text-[13px] font-medium leading-4 tracking-[-0.13px] transition-colors',
                  isActive
                    ? 'text-v2-text-primary'
                    : 'text-v2-text-secondary hover:text-v2-text-primary',
                )}
              >
                {tab.label}
              </button>
            );
          })}
          {indicator !== null && (
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-0 h-px bg-v2-text-primary"
              style={{
                left: indicator.left,
                width: indicator.width,
                transition: 'left 220ms ease-in-out, width 220ms ease-in-out',
              }}
            />
          )}
        </div>
      )}

      <div>
        {/* All data tabs are always mounted with `hidden` toggling visibility.
          AnimatePresence with exit animations would temporarily keep two blocks
          in the DOM at once, pushing other content (like the OrderBook) down
          mid-switch. Static visibility toggling avoids any layout shift. */}
        {showPositionsTabs && (
          <div className={cn('flex flex-col gap-4', activeTab !== 'openPositions' && 'hidden')}>
            <div className="hidden min-[768px]:block">
              <EventOpenPositionsTable />
            </div>
            <div className="flex flex-col gap-4 min-[768px]:hidden">
              <EventOpenPositionsList />
            </div>
          </div>
        )}

        <div className={cn('flex flex-col gap-4', activeTab !== 'openOrders' && 'hidden')}>
          <div className="hidden min-[768px]:block">
            <EventOpenOrdersTable outcomeId={outcomeId} isTabActive={activeTab === 'openOrders'} />
          </div>
          <div className="flex flex-col gap-4 min-[768px]:hidden">
            <EventOpenOrdersList />
          </div>
        </div>

        <div className={cn('flex flex-col gap-4', activeTab !== 'history' && 'hidden')}>
          <div className="hidden min-[361px]:block">
            <EventHistoryTable />
          </div>
          <div className="flex flex-col gap-4 min-[361px]:hidden">
            {Array.from({ length: 6 }).map((_, index) => (
              <EventHistoryCard key={index} />
            ))}
          </div>
        </div>

        {showChartTab && (
          <div className={cn('relative w-full pt-4', activeTab !== 'chart' && 'hidden')}>
            <OutcomeChart outcomeId={outcome.id} />
          </div>
        )}

        {activeTab === 'resolution' && outcome.r && outcome.r.length > 0 && (
          <ResolutionTimeline resolutions={outcome.r} />
        )}

        {/* Keep OrderBook mounted to avoid re-initialization flicker */}
        <div className={cn('flex flex-col gap-4', activeTab !== 'orderBook' && 'hidden')}>
          <OrderBook
            outcomeId={outcome.id}
            containerClassName="h-[436px]"
            scrollClassName="h-[400px]"
          />
        </div>
      </div>
    </div>
  );
};
